import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as Bridge from '@/api/BridgeApi'
import { MultiLineText } from '@/components/common/i18n'
import * as Layer from '@/components/common/layer'
import {
  BILL_PAYMENT_ROUTES,
  DEBIT_CARD_ROUTES,
  ENTRY_ROUTE_QUERY,
  SCHEDULE_PAYMENT_ROUTES,
  TOP_NATIVE_ROUTES,
} from '@/domains/CommonDomain'
import { useTrans } from '@/hooks/useTrans'
import { IRootState } from '@/modules'
import { simpleAccountUpdateAsync } from '@/modules/account/SimpleAccountModule'
import { billMainActions } from '@/modules/bill-payment/BillPaymentMainModule'
import { ILayerState, LAYER, layerActions } from '@/modules/common/layer'
import {
  debitCardIssueSelector,
  IDebitCardIssueView,
} from '@/modules/debit-card/DebitCardIssueModule'
/* TODO: remove referral-promotion */
import { IInviteState, INVITE } from '@/modules/promotion/InviteModule'
import {
  schedulePaymentRegisterActions,
  schedulePaymentRegisterSelector,
} from '@/modules/schedule-payment/SchedulePaymentRegisterModule'
import { schedulePaymentSendActions } from '@/modules/schedule-payment/SchedulePaymentSendModule'
import { billMainActions as billMainBetaActions } from '@/modules/version/V2/bill-payment/BillPaymentMainModule'
import BridgeUtils from '@/utils/bridgeUtils'
import browserHistory, { goBack, push, redirect } from '@/utils/historyUtils'

import { Message } from './Message'
import { Title } from './Title'

export enum LEAVE_ALERT_TYPE {
  LOW_BALANCE = 'LOW_BALANCE',
  NO_ADDITIONAL = 'NO_ADDITIONAL',
  OPEN_ACCOUNT = 'OPEN_ACCOUNT',
  DEBIT_CARD_ISSUE_CANCEL = 'DEBIT_CARD_ISSUE_CANCEL',
  DEBIT_CARD_ISSUE_DELAY_NOTICE = 'DEBIT_CARD_ISSUE_DELAY_NOTICE',
  DEBIT_CARD_ISSUE_HLI_ERROR = 'DEBIT_CARD_ISSUE_HLI_ERROR',
  BILL_PAYMENT = 'BILL_PAYMENT',
  SCHEDULE_PAYMENT = 'SCHEDULE_PAYMENT',
  CANCEL_ACTIVATE_CARD = 'CANCEL_ACTIVATE_CARD',
  INVALID_CARRIER = 'INVALID_CARRIER',
  BILL_PAYMENT_TRANSACTION_FAIL = 'BILL_PAYMENT_TRANSACTION_FAIL',
  BILL_PAYMENT_INVALID_ACCCOUNT_STATUS = 'BILL_PAYMENT_INVALID_ACCCOUNT_STATUS',
  BILL_PAYMENT_INSUFFICIENT_BALANCE = 'BILL_PAYMENT_INSUFFICIENT_BALANCE',
  BILL_PAYMENT_QUICK_CREDIT_DISBURSED = 'BILL_PAYMENT_QUICK_CREDIT_DISBURSED',
  BILL_PAYMENT_QUICK_CREDIT_DISBURSEMENT_FAILED = 'BILL_PAYMENT_QUICK_CREDIT_DISBURSEMENT_FAILED',
  BILL_PAYMENT_TRANSACTION_PENDING = 'BILL_PAYMENT_TRANSACTION_PENDING',
  INVALID_BILL_PAYMENT_INFO = 'INVALID_BILL_PAYMENT_INFO',
  CANCEL_RESET_LOCKED_DEBIT_CARD_PIN = 'CANCEL_RESET_LOCKED_DEBIT_CARD_PIN',
  CANCEL_RESET_DEBIT_CARD_PIN = 'CANCEL_RESET_DEBIT_CARD_PIN',
  INVALID_DEBIT_CARD_INFO = 'INVALID_DEBIT_CARD_INFO',
  INVALID_RECENTLY_FAVORITE_ACCOUNT_NO = 'INVALID_RECENTLY_FAVORITE_ACCOUNT_NO',
  SCHEDULE_PAYMENT_START_DATE_ERROR = 'SCHEDULE_PAYMENT_START_DATE_ERROR',
  SCHEDULE_PAYMENT_END_DATE_ERROR = 'SCHEDULE_PAYMENT_END_DATE_ERROR',
  SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR = 'SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR',
  SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR = 'SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR',
  SCHEDULE_PAYMENT_START_MONTH_ERROR = 'SCHEDULE_PAYMENT_START_MONTH_ERROR',
  SCHEDULE_PAYMENT_END_MONTH_ERROR = 'SCHEDULE_PAYMENT_END_MONTH_ERROR',
  SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR = 'SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR',
  SCHEDULE_PAYMENT_MONTH_DATE_ERROR = 'SCHEDULE_PAYMENT_MONTH_DATE_ERROR',
  NOT_AVAILABLE_REPORT_CARD = 'NOT_AVAILABLE_REPORT_CARD',
  /* TODO: remove referral-promotion */
  PROMOTION = 'PROMOTION',
  PROMOTION_SENT = 'PROMOTION_SENT',

  MGM_NO_PROMO_URL = 'MGM_NO_PROMO_URL',
}

export const LeaveLayerInvalidAccount = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.INVALID_RECENTLY_FAVORITE_ACCOUNT_NO]
  const closeLayer = useCallback(() => {
    dispatch(schedulePaymentSendActions.closeInvalidAccountLayer())
  }, [dispatch])
  const { bnfcBankNm, bnfcAcctNo } = useSelector(schedulePaymentRegisterSelector.allState)

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('transferoption_notexist_alert', [bnfcBankNm, bnfcAcctNo])}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerLowBalance = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.LOW_BALANCE]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.LOW_BALANCE))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_confirm_noamount_popup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerNoAdditional = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.NO_ADDITIONAL]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.NO_ADDITIONAL))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_linkedacc_selectacc_noadd_pop_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerOpenAccount = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.OPEN_ACCOUNT]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.OPEN_ACCOUNT))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_linkedacc_selectacc_noadd_pop_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerDebitCardIssue = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const { selectedDebitCardNo, selectedAccount } = useSelector(debitCardIssueSelector.view)
  const { isReissue } = useSelector(debitCardIssueSelector.mode)

  const isOpen = layerStatus[LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_CANCEL]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_CANCEL))
  }, [dispatch])
  const onExit = useCallback(() => {
    const debitCardDetailPath = `${DEBIT_CARD_ROUTES.DETAIL}/${selectedDebitCardNo}/${selectedAccount.acctNo}?${ENTRY_ROUTE_QUERY}`

    dispatch(layerActions.close(LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_CANCEL))
    isReissue ? browserHistory.replace(debitCardDetailPath) : Bridge.closeWebView()
  }, [dispatch, isReissue, selectedAccount, selectedDebitCardNo])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_exit_popup_desc_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_discard_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerBillPayment = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT]

  if (isOpen && layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT))
    // } else if (isOpen) {
    //   dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT))
  }

  const onCancel = useCallback(() => dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT)), [
    dispatch,
  ])
  const onExit = useCallback(() => Bridge.closeWebView(), [])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('billpayment_exit_popup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

/* Quick Credit Disbursement Success, Transaction Failed */
export const LeaveLayerBillPaymentQuickCreditDisbursed = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const type = LEAVE_ALERT_TYPE.BILL_PAYMENT_QUICK_CREDIT_DISBURSED
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[type]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(type))
    push(BILL_PAYMENT_ROUTES.DISBURSED)
  }, [dispatch, type])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Layer.Leave>
        <Title>{$('billpayment_quick_credit_trx_failed_popup_title')}</Title>
        <Message>
          <MultiLineText>{$('billpayment_quick_credit_trx_failed_popup_desc')}</MultiLineText>
        </Message>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose}>{$('common_ok_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

/*  Quick Credit Disbursement Failed, Transaction Failed */
export const LeaveLayerBillPaymentQuickCreditDisbursementFailed = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const type = LEAVE_ALERT_TYPE.BILL_PAYMENT_QUICK_CREDIT_DISBURSEMENT_FAILED
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[type]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(type))
  }, [dispatch, type])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Layer.Leave>
        <Title>{$('billpayment_quick_credit_disbursement_failed_popup_title')}</Title>
        <Message>
          <MultiLineText>
            {$('billpayment_quick_credit_disbursement_failed_popup_desc')}
          </MultiLineText>
        </Message>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose}>{$('common_ok_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerBillPaymentTransactionPending = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const type = LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_PENDING
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[type]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(type))
  }, [dispatch, type])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Layer.Leave>
        <Title>{$('billpayment_transaction_pending_popup_title')}</Title>
        <Message>
          <MultiLineText>{$('billpayment_transaction_pending_popup_desc')}</MultiLineText>
        </Message>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose}>{$('common_ok_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePayment = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT]

  if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_DATE_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_DATE_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (
    isOpen &&
    layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR]
  ) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_MONTH_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_MONTH_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (
    isOpen &&
    layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR]
  ) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  } else if (isOpen && layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR]) {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR))
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
  }

  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT)),
    [dispatch],
  )
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT))
    dispatch(schedulePaymentRegisterActions.reset())
    redirect(`${SCHEDULE_PAYMENT_ROUTES.ROOT}?${ENTRY_ROUTE_QUERY}`)
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('schedulepayment_cancelpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_no_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_yes_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerCancelActivateCard = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.CANCEL_ACTIVATE_CARD]

  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_ACTIVATE_CARD)),
    [dispatch],
  )
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_ACTIVATE_CARD))
    Bridge.navigateTo(TOP_NATIVE_ROUTES.ROOT)
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Title>{$('debitcard_cancelactcard_exit_popup_title_alert')}</Title>
        <Message>{$('debitcard_cancelactcard_exit_popup_desc_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_discard_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInvalidCarrier = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.INVALID_CARRIER]
  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.INVALID_CARRIER)),
    [dispatch],
  )

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('billpayment_pulsa_phonebook_nomatche_carrier_popup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerTransactionFail = (props: {
  customerId: string
  productTypeName: string
  useTransition?: boolean
}) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const { productTypeName, customerId } = props
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL]

  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    goBack()
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Title>
          {/* {$('billpayment_payment_failed_pop_desc1', [productTypeName, customerId])} */}
          {`${productTypeName} ${customerId}`}
        </Title>
        <Message>{$('billpayment_payment_failed_pop_desc2')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInvalidAccountStatus = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS))
    goBack()
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message isBold>{$('billpayment_confirm_error_account_status_pop_desc')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInsufficientBalance = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE]
  const onCancel = useCallback(() => {
    dispatch(simpleAccountUpdateAsync.fetch())
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message isBold>{$('billpayment_confirm_insufficient_pop_desc')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInvalidBillPaymentInfo = (props: {
  useTransition?: boolean
  customerId: string
  productTypeName: string
}) => {
  const $ = useTrans()
  const { productTypeName, customerId } = props
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.INVALID_BILL_PAYMENT_INFO]
  const onCancel = useCallback(
    () => dispatch(billMainActions.resetPayStateByRecentlyAndFavoriteMenu()),
    [dispatch],
  )

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message isBold>
          {$('billpayment_main_recent_notexist_pop_desc', [productTypeName, customerId])}
        </Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInvalidBillPaymentInfoBeta = (props: {
  useTransition?: boolean
  customerId: string
  productTypeName: string
}) => {
  const $ = useTrans()
  const { productTypeName, customerId } = props
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.INVALID_BILL_PAYMENT_INFO]
  const onCancel = useCallback(() => {
    dispatch(billMainBetaActions.resetPayStateByRecentlyAndFavoriteMenu())
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message isBold>
          {unescape($('billpayment_main_recent_notexist_pop_desc', [productTypeName, customerId]))}
        </Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerCancelResetLockedDebitCardPin = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.CANCEL_RESET_LOCKED_DEBIT_CARD_PIN]

  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_RESET_LOCKED_DEBIT_CARD_PIN)),
    [dispatch],
  )
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_RESET_LOCKED_DEBIT_CARD_PIN))
    Bridge.navigateTo(TOP_NATIVE_ROUTES.ROOT)
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_pin_locked_reset_exit_popup_desc_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_discard_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerCancelResetDebitCardPin = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.CANCEL_RESET_DEBIT_CARD_PIN]

  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_RESET_DEBIT_CARD_PIN)),
    [dispatch],
  )
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_RESET_DEBIT_CARD_PIN))
    Bridge.navigateTo(TOP_NATIVE_ROUTES.ROOT)
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Title>{$('debitcard_pin_setcancel_exit_popup_title_alert')}</Title>
        <Message>{$('debitcard_pin_setcancel_exit_popup_desc_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_discard_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerInvalidDebitCardInfo = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.INVALID_DEBIT_CARD_INFO]
  const onCancel = useCallback(
    () => dispatch(layerActions.close(LEAVE_ALERT_TYPE.INVALID_DEBIT_CARD_INFO)),
    [dispatch],
  )
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.CANCEL_RESET_DEBIT_CARD_PIN))
    Bridge.navigateTo(TOP_NATIVE_ROUTES.ROOT)
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_mycard_invalid_card_info_desc')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onExit}>
            {$('common_discard_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentStartDateError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledatestartdate_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentStartDateAndEndDateError = (props: {
  useTransition?: boolean
}) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledatestartdate_enddate_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentEndDateError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_DATE_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_DATE_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledateenddate_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentWeekendDateError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledateweekenddate_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentStartMonthError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledatestartmonth_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentEndMonthError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_MONTH_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_MONTH_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledateendmonth_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentStartMonthAndEndMonthError = (props: {
  useTransition?: boolean
}) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledatestartmonth_endmonth_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerSchedulePaymentMonthError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('scheduledateendmonth_enddate_errorpopup_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerNotAvailableReportCard = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.NOT_AVAILABLE_REPORT_CARD]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.NOT_AVAILABLE_REPORT_CARD))
  }, [dispatch])
  const onCallCenter = () => {
    Bridge.callCenter()
  }

  useEffect(() => {
    Bridge.onBackPressed(() => {
      if (!isOpen) {
        goBack()
      }
    })
  }, [isOpen])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_mycard_main_locked_noreport_desc_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('common_cancel_button')}
          </Layer.Button>
          <Layer.Button isType3 onClick={onCallCenter}>
            {$('debitcard_mycard_callcenter_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

/* TODO: remove referral-promotion */
export const PromotionError = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.PROMOTION]
  const closeLayer = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.PROMOTION))
  }, [dispatch])

  const { code, message } = useSelector<IRootState, IInviteState>(state => state[INVITE])

  return (
    <Layer.Wrapper isOpen={isOpen} close={closeLayer} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{`code: ${code}\n${
          message === '' ? $('common_error_nouserdata_desc') : message
        }`}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={closeLayer}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

/* TODO: remove referral-promotion */
export const LeaveLayerPromotionSent = (props: { useTransition?: boolean }) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])

  const isOpen = layerStatus[LEAVE_ALERT_TYPE.PROMOTION_SENT]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.PROMOTION_SENT))
  }, [dispatch])
  const onExit = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.PROMOTION_SENT))
    Bridge.closeWebView()
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('referral_sent_close_desc')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isType2 onClick={onCancel}>
            {$('referral_sent_close_cancel_button')}
          </Layer.Button>
          <Layer.Button onClick={onExit}>{$('referral_sent_close_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerCardDelayNotice = (props: {
  useTransition?: boolean
  callbackOnCancel?: Function
}) => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const useTransition = props?.useTransition ?? true
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_DELAY_NOTICE]
  const onCancel = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_DELAY_NOTICE))
    if (props.callbackOnCancel) {
      props.callbackOnCancel()
    }
  }, [dispatch, props])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onCancel} useTransition={useTransition}>
      <Layer.Leave>
        <Message>{$('debitcard_issue_delay_notice_pop_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button isOk onClick={onCancel}>
            {$('common_ok_button')}
          </Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerDebitCardIssueHLIError = () => {
  /* 카드발행 시, check issuance fee 실패해도 페이지에 남아있으면 다음 단계 진행 가능 -> WebView 를 닫아서 진행 막기  */
  const $ = useTrans()
  const dispatch = useDispatch()
  const { errorCode, errorMessage } = useSelector<IRootState, IDebitCardIssueView>(
    debitCardIssueSelector.view,
  )
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_HLI_ERROR]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.DEBIT_CARD_ISSUE_HLI_ERROR))
    BridgeUtils.closeWebView()
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Layer.Leave>
        <Message>{errorMessage}</Message>
        <Layer.Code>{errorCode}</Layer.Code>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose}>{$('common_ok_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}

export const LeaveLayerMgmNoPromoUrl = () => {
  const $ = useTrans()
  const dispatch = useDispatch()
  const layerStatus = useSelector<IRootState, ILayerState>(state => state[LAYER])
  const isOpen = layerStatus[LEAVE_ALERT_TYPE.MGM_NO_PROMO_URL]
  const onClose = useCallback(() => {
    dispatch(layerActions.close(LEAVE_ALERT_TYPE.MGM_NO_PROMO_URL))
  }, [dispatch])

  return (
    <Layer.Wrapper isOpen={isOpen} close={onClose}>
      <Layer.Leave>
        <Message>{$('mgm_promotion_no_url_alert')}</Message>
        <Layer.ButtonBox>
          <Layer.Button onClick={onClose}>{$('common_ok_button')}</Layer.Button>
        </Layer.ButtonBox>
      </Layer.Leave>
    </Layer.Wrapper>
  )
}
